/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, { useState, useRef, useEffect } from 'react'
import {
    Box,
    Text,
    SimpleGrid,
    useMultiStyleConfig,
    createStylesContext
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import { useGlobalFooter } from '../../contexts/global-elements-context'
import { noop } from '@salesforce/retail-react-app/app/utils/utils'
import { getHideStyles } from '../optimizely/utils/utils'
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/* Components */
import { HideOnDesktop, HideOnMobile } from '@salesforce/retail-react-app/app/components/responsive'
import NewsletterSignUp from './partial/newsletterSignUp'
import NavigationLinks from './partial/navigationLinks'
import FooterCountrySelector from './partial/footerCountrySelector'

const [StylesProvider] = createStylesContext('Footer')

// ESW Custom Imports
import { EswInit } from '../../esw/components/esw-init'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
// End ESW Custom Imports


const Footer = ({ onShowCountrySelectorOnFooter = noop, ...otherProps }) => {
    const { app: appConfig } = getConfig()
    const location = useLocation()
    const [isPDP, setIsPDP] = useState(false)
    const styles = useMultiStyleConfig('Footer', { isPDP })
    const [_showCountrySelectorOnFooter, setShowCountrySelectorOnFooter] = useState(false)
    const { data: footerData } = useGlobalFooter()
    const footerContainerRef = useRef(null)
    const [hideStyles, setHideStyles] = useState({})
    const { site } = useMultiSite()
    const intl = useIntl()
    const [locale, setLocale] = useState(intl.locale)
    const isNewsletterEnabled = appConfig?.SFSC_EMAIL_SUBSCRIPTION_ENABLED === 'true'
    const handleShowCountrySelectorOnFooter = (showCSOnFooter) => {
        setShowCountrySelectorOnFooter(showCSOnFooter)
        onShowCountrySelectorOnFooter(showCSOnFooter)
    }

    const isESWEnabled = appConfig?.ESW_ESHOPWORLD_MODULE_ENABLED

    useEffect(() => {
        const footerContainerEl = footerContainerRef?.current
        const appContainer = footerContainerEl.closest('#app')
        const isPDPPage = appContainer.querySelectorAll('[data-is-pdp]')?.length > 0
        setIsPDP(isPDPPage)
    }, [])

    useEffect(() => {
        setHideStyles(getHideStyles(footerData?.raw?.HideOnDevices))
    }, [footerData])

    if (hideStyles.doNotLoadComponent) return

    return (
        <Box
            ref={footerContainerRef}
            as="footer"
            {...styles.container}
            {...otherProps}
            {...hideStyles}
        >
            <Box {...styles.content} as="section">
                <StylesProvider value={styles}>
                    {isNewsletterEnabled && (
                        <HideOnDesktop>
                            <NewsletterSignUp {...styles.subscribeContent} />
                        </HideOnDesktop>
                    )}

                    <SimpleGrid columns={{ base: 1, lg: 3 }}>
                        <Box gridColumn={{ base: 'span 1', lg: 'span 2' }}>
                            <SimpleGrid
                                columns={{ base: 1, lg: 4 }}
                                spacing={{ lg: 2 }}
                                {...styles.linksContent}
                            >
                                <NavigationLinks footerData={footerData} />
                            </SimpleGrid>
                        </Box>
                        {isNewsletterEnabled && (
                            <HideOnMobile
                                gridColumn={{ base: 'span 1', lg: 'span 1' }}
                                w={{ lg: '100%' }}
                                {...styles.subscribeContent}
                            >
                                <Box
                                    display={{ lg: 'flex' }}
                                    flexDir={{ lg: 'column' }}
                                    justifyContent={{ lg: 'center' }}
                                    height={{ lg: '100%' }}
                                >
                                    <NewsletterSignUp />
                                </Box>
                            </HideOnMobile>
                        )}
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, lg: 3 }}>
                        <Box
                            {...styles.copyrightContent}
                            gridColumn={{
                                base: 'span 1',
                                lg: _showCountrySelectorOnFooter ? 'span 2' : 'span 3'
                            }}
                            order={{ base: 1, lg: 0 }}
                        >
                            <Text {...styles.copyright}>
                                {footerData?.raw?.CoyprightText ? (
                                    <>
                                        {footerData.raw.CoyprightText}
                                        {footerData?.raw?.VatNumber && (
                                            <Text as="span" {...styles.vat}>
                                                {footerData.raw.VatNumber}
                                            </Text>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <FormattedMessage
                                            defaultMessage="Groupe Dynamite. All rights reserved ©{year}"
                                            id="footer.message.copyright"
                                            values={{ year: new Date().getFullYear() }}
                                        />
                                        <Text as="span" {...styles.vat}>
                                            <FormattedMessage
                                                defaultMessage="VAT #485 5347 55"
                                                id="footer.message.vat"
                                                values={{ year: new Date().getFullYear() }}
                                            />
                                        </Text>
                                    </>
                                )}
                            </Text>
                        </Box>
                        <Box
                            order={{ base: 0, lg: 1 }}
                            padding={{ base: '24px 16px', lg: '16px 24px' }}
                            display={_showCountrySelectorOnFooter ? 'block' : 'none'}
                            gridColumn={{ base: 'span 1', lg: 'span 1' }}
                            borderTop={{ base: '1px solid' }}
                            borderLeft={{ base: '1px solid' }}
                            borderColor={{ base: 'grey.400' }}
                        >
                            <FooterCountrySelector
                                onShowCountrySelectorOnFooter={handleShowCountrySelectorOnFooter}
                            />
                        </Box>
                    </SimpleGrid>
                </StylesProvider>
            </Box>
            {isESWEnabled && (
                <EswInit site={site} locale={locale} />
            )}
        </Box>
    )
}

export default Footer
